import { DealTroubleshootingTimeResolutionEnum } from '@openx/types/gqlTypes.ts';
import type { ConstValues } from '@openx/types/helpers.ts';
import type { TroubleshootingFunnelApi } from '@openx/types/troubleshootingFunnel.ts';
import { subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type { TroubleshootingParams } from '../../utils/hooks/useTroubleshootingFetch.ts';
import type { DateRange } from './components/TroubleshootingDateFilter.tsx';

export const DSP_OPTION_ALL = 'dsp_option_all';

export const funnelRenderState = {
  DATA_AVAILABLE: 'data_available',
  ERROR: 'error',
  NO_DATA: 'no_data',
} as const;
export type FunnelRenderState = ConstValues<typeof funnelRenderState>;

export const hasFunnelData = (funnelData?: TroubleshootingFunnelApi | null): boolean => {
  if (!funnelData) return false;
  return (
    funnelData.spend !== null ||
    funnelData.cpm !== null ||
    funnelData.targeted_bid_requests !== null ||
    funnelData.bid_responses !== null ||
    funnelData.fills !== null ||
    funnelData.impressions !== null ||
    funnelData.eligible_bid_requests !== null ||
    funnelData.ctr !== null ||
    funnelData.vtr !== null ||
    funnelData.viewability !== null ||
    Boolean(funnelData.upshot_codes && Object.keys(funnelData.upshot_codes).length > 0) ||
    Boolean(funnelData.time_series_stats && funnelData.time_series_stats.length > 0)
  );
};

export const timePeriod = {
  Last24Hours: '24h',
  Last7Days: '7d',
  Last30Days: '30d',
  Yesterday: 'yesterday',
  Custom: 'custom',
} as const;

export type TimePeriod = ConstValues<typeof timePeriod>;

// Base config properties shared by all config types
interface BaseTimePeriodConfig {
  value: TimePeriod;
  labelKey: string;
  testId: string;
}

// NRT jobs - uses lookback hours
interface NrtTimePeriodConfig extends BaseTimePeriodConfig {
  lookbackHours: number;
  subtractDays?: never;
  resolution?: never;
  isCustom?: never;
}

// Fact jobs - uses date ranges with subtractDays and resolution
interface FactTimePeriodConfig extends BaseTimePeriodConfig {
  subtractDays: number;
  resolution: DealTroubleshootingTimeResolutionEnum;
  lookbackHours?: never;
  isCustom?: never;
}

// Custom date range - uses isCustom flag and optional resolution
interface CustomTimePeriodConfig extends BaseTimePeriodConfig {
  isCustom: true;
  resolution?: DealTroubleshootingTimeResolutionEnum;
  lookbackHours?: never;
  subtractDays?: never;
}

// Discriminated union - must have either lookbackHours OR (subtractDays + resolution) OR isCustom
export type TimePeriodConfig = NrtTimePeriodConfig | FactTimePeriodConfig | CustomTimePeriodConfig;

export const TIME_PERIOD_CONFIGS = [
  {
    value: timePeriod.Last24Hours,
    labelKey: 'Last 24 hours',
    testId: 'time-filter-24h',
    lookbackHours: 24,
  },
  {
    value: timePeriod.Yesterday,
    labelKey: 'Yesterday',
    testId: 'time-filter-yesterday',
    subtractDays: 0,
    resolution: DealTroubleshootingTimeResolutionEnum.Hourly,
  },
  {
    value: timePeriod.Last7Days,
    labelKey: 'Last 7 Days',
    testId: 'time-filter-7d',
    subtractDays: 7,
    resolution: DealTroubleshootingTimeResolutionEnum.Daily,
  },
  {
    value: timePeriod.Last30Days,
    labelKey: 'Last 30 Days',
    testId: 'time-filter-30d',
    subtractDays: 30,
    resolution: DealTroubleshootingTimeResolutionEnum.Daily,
  },
  {
    value: timePeriod.Custom,
    labelKey: 'Custom',
    testId: 'time-filter-custom',
    isCustom: true,
    resolution: DealTroubleshootingTimeResolutionEnum.Daily,
  },
] satisfies TimePeriodConfig[];

export const DEFAULT_TIME_PERIOD: TimePeriod = timePeriod.Last7Days;

/**
 * Get troubleshooting params for a given time period
 */
export const getTroubleshootingParams = (
  period: TimePeriod,
  dspId?: string,
  customDateRange?: DateRange
): TroubleshootingParams | undefined => {
  const config = TIME_PERIOD_CONFIGS.find(c => c.value === period);
  if (!config) {
    return undefined;
  }

  const effectiveDspId = dspId === DSP_OPTION_ALL ? undefined : dspId;

  // Custom date range
  if (config.isCustom && customDateRange) {
    return {
      startDate: formatInTimeZone(customDateRange.startDate, 'UTC', 'yyyy-MM-dd'),
      endDate: formatInTimeZone(customDateRange.endDate, 'UTC', 'yyyy-MM-dd'),
      dspId: effectiveDspId,
      resolution: DealTroubleshootingTimeResolutionEnum.Daily,
    };
  }

  if (config.isCustom) {
    return undefined;
  }

  // Lookback hours (NRT jobs)
  if (config.lookbackHours) {
    return {
      lookbackHours: config.lookbackHours,
      dspId: effectiveDspId,
    };
  }

  // Date ranges (Fact jobs)
  const now = new Date();
  const yesterday = subDays(now, 1);

  if (config.subtractDays !== undefined) {
    const startDate = config.subtractDays === 0 ? yesterday : subDays(now, config.subtractDays);

    return {
      startDate: formatInTimeZone(startDate, 'UTC', 'yyyy-MM-dd'),
      endDate: formatInTimeZone(yesterday, 'UTC', 'yyyy-MM-dd'),
      dspId: effectiveDspId,
      resolution: config.resolution ?? DealTroubleshootingTimeResolutionEnum.Daily,
    };
  }

  return undefined;
};

/**
 * Get date range for CSV export
 */
export const getDateRangeForExport = (
  period: TimePeriod,
  customDateRange?: DateRange
): { startDate?: string; endDate?: string } => {
  if (period === timePeriod.Last24Hours) {
    // For Last 24 Hours, calculate the actual date range (24 hours ago to now)
    const now = new Date();
    const twentyFourHoursAgo = subDays(now, 1);
    return {
      startDate: formatInTimeZone(twentyFourHoursAgo, 'UTC', 'yyyy-MM-dd'),
      endDate: formatInTimeZone(now, 'UTC', 'yyyy-MM-dd'),
    };
  }

  if (period === timePeriod.Custom && customDateRange) {
    return {
      startDate: formatInTimeZone(customDateRange.startDate, 'UTC', 'yyyy-MM-dd'),
      endDate: formatInTimeZone(customDateRange.endDate, 'UTC', 'yyyy-MM-dd'),
    };
  }

  const config = TIME_PERIOD_CONFIGS.find(c => c.value === period);
  if (!config) {
    return {};
  }

  const now = new Date();
  const yesterday = subDays(now, 1);

  if (config.subtractDays !== undefined) {
    const startDate = config.subtractDays === 0 ? yesterday : subDays(now, config.subtractDays);

    return {
      startDate: formatInTimeZone(startDate, 'UTC', 'yyyy-MM-dd'),
      endDate: formatInTimeZone(yesterday, 'UTC', 'yyyy-MM-dd'),
    };
  }

  return {};
};

/**
 * Get tooltip text for a time period
 */
export const getTooltipText = (
  period: TimePeriod,
  lastUpdated: string | null | undefined,
  t: (key: string, options?: { date: string }) => string
): string => {
  if (!lastUpdated) return '';

  if (period === timePeriod.Last24Hours) {
    const formattedDate = formatInTimeZone(new Date(lastUpdated), 'UTC', 'yyyy-MM-dd hh:mm a');
    return t(
      'Data was last updated on {date} (UTC). Some of the metrics are not available for the last 24 hours date range.',
      {
        date: formattedDate,
      }
    );
  }

  const formattedDate = formatInTimeZone(new Date(lastUpdated), 'UTC', 'yyyy-MM-dd');
  return t('Data was last updated on {date}.', { date: formattedDate });
};
